import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button, Card, List, Popconfirm, Space, Tag, Typography, message } from 'antd';
import { AppstoreAddOutlined } from '@ant-design/icons';
import { useApi } from '../../hooks/use_api';
import { useNodeApps } from '../../hooks/use_node_apps';
import { useNodeInfo } from '../../hooks/use_node_info';
import InstallAppModal from '../Apps/InstallAppModal';

const { Title, Text, Paragraph } = Typography;

/**
 * Superadmin-only: the apps installed in the node itself. One installed here
 * is for everyone signed in on this node — it appears in their nav bar
 * (use_node_apps) and runs with every scope its manifest asks for — so
 * installing it is the node approving that manifest (POST /api/apps/node).
 * Apps installed in a person, context or relationship are managed from that
 * entity's page, not here.
 */
function NodeApps() {
    const api = useApi();
    const { apps, ready, refresh } = useNodeApps();
    const { nodeName } = useNodeInfo();
    const [installOpen, setInstallOpen] = useState(false);
    const [busy, setBusy] = useState(null);

    const uninstall = inst => {
        setBusy(inst.id);
        api.del(`/api/apps/${inst.id}/node`)
            .then(() => { message.success(`${inst.name} uninstalled from the node`); return refresh(); })
            .catch(err => message.error(err.status === 403 ? 'Only a superadmin can uninstall an app from the node' : (err.message || 'Could not uninstall')))
            .finally(() => setBusy(null));
    };

    return (
        <Card loading={!ready}>
            <Title level={5} style={{ marginTop: 0 }}>Apps in this node</Title>
            <Paragraph type="secondary">
                An app installed here is for everyone signed in on this node: it appears in their
                navigation and runs with every scope its manifest asks for. Apps installed in a
                person, context or relationship are managed from that entity's page instead.
            </Paragraph>
            <List
                dataSource={apps}
                locale={{ emptyText: 'No apps are installed in this node' }}
                renderItem={inst => (
                    <List.Item actions={[
                        <Popconfirm
                            key="uninstall"
                            title="Uninstall from the node?"
                            description="It stays installed in any entity that hosts it; otherwise it stops at once."
                            onConfirm={() => uninstall(inst)}
                            okText="Uninstall"
                        >
                            <Button type="link" danger loading={busy === inst.id} style={{ padding: 0 }}>Uninstall</Button>
                        </Popconfirm>,
                    ]}>
                        <List.Item.Meta
                            title={<Space size={6}><Link to={`/app/${inst.id}`}>{inst.name}</Link><Tag>{inst.kind}</Tag></Space>}
                            description={
                                <Text type="secondary" style={{ fontSize: 12 }}>
                                    Version {inst.version} · may: {inst.approved_scopes.join(', ') || 'nothing beyond itself'}
                                    {inst.host_ids.length > 0 && ` · also installed in ${inst.host_ids.length} ${inst.host_ids.length === 1 ? 'entity' : 'entities'}`}
                                </Text>
                            }
                        />
                    </List.Item>
                )}
            />
            <Button icon={<AppstoreAddOutlined />} onClick={() => setInstallOpen(true)} style={{ marginTop: 12 }}>
                Install app
            </Button>
            <InstallAppModal
                open={installOpen}
                onClose={() => setInstallOpen(false)}
                nodeWide
                hostName={nodeName || 'this node'}
                onInstalled={refresh}
            />
        </Card>
    );
}

export default NodeApps;
